import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Scale, UserCheck, Lock, ShieldCheck, UserPlus, Eye, Download, ArrowLeft, Loader2,
  FileText, Sparkles, Mail, Send, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => {
  const t = localStorage.getItem("admin_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ICONS = {
  "scale":         Scale,
  "user-check":    UserCheck,
  "lock":          Lock,
  "shield-check":  ShieldCheck,
  "user-plus":     UserPlus,
};

export function DocumentStudioTab() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected]   = useState(null);   // template object
  const [formData, setFormData]   = useState({});
  const [previewUrl, setPreviewUrl] = useState(null); // blob URL
  const [previewBlob, setPreviewBlob] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [generating, setGenerating] = useState(false);

  // Email compose state
  const [composeOpen, setComposeOpen] = useState(false);
  const [drafting, setDrafting]   = useState(false);
  const [sending, setSending]     = useState(false);
  const [emailTo, setEmailTo]     = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [fromName, setFromName]   = useState("");
  const [bccMe, setBccMe]         = useState(true);

  // Fetch templates once
  useEffect(() => {
    axios.get(`${API}/studio/templates`, { headers: authHeader() })
      .then(({ data }) => setTemplates(data.items || []))
      .catch(() => toast.error("Failed to load templates"));
  }, []);

  // When a template is picked, hydrate defaults
  const pickTemplate = (tpl) => {
    const initial = {};
    tpl.fields.forEach((f) => { initial[f.key] = f.default ?? ""; });
    setSelected(tpl);
    setFormData(initial);
    setPreviewUrl(null);
    setPreviewBlob(null);
  };

  const goBack = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelected(null);
    setFormData({});
    setPreviewUrl(null);
    setPreviewBlob(null);
  };

  // Validate required fields
  const missing = selected
    ? selected.fields.filter((f) => f.required && !String(formData[f.key] || "").trim())
    : [];

  // POST to /studio/generate, get PDF blob, show in iframe
  const generatePreview = useCallback(async () => {
    if (!selected) return;
    if (missing.length) {
      toast.error(`Fill required: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/studio/generate`,
        { template_id: selected.id, data: formData },
        { headers: { ...authHeader(), "Content-Type": "application/json" }, responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewBlob(blob);
      toast.success("Preview generated");
    } catch (e) {
      toast.error("Preview failed. Check required fields.");
    } finally {
      setGenerating(false);
    }
  }, [selected, formData, missing, previewUrl]);

  const downloadPdf = () => {
    if (!previewBlob || !selected) return;
    const a = document.createElement("a");
    const url = URL.createObjectURL(previewBlob);
    a.href = url;
    const clientPart = (formData.client_name || formData.candidate_name || "document").replace(/\s+/g, "_");
    a.download = `MK_${selected.title.replace(/\s+/g, "_")}_${clientPart}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Fetch an AI draft for the email body
  const fetchDraft = useCallback(async () => {
    if (!selected) return;
    setDrafting(true);
    try {
      const { data } = await axios.post(
        `${API}/studio/draft-email`,
        { template_id: selected.id, data: formData },
        { headers: { ...authHeader(), "Content-Type": "application/json" } },
      );
      setEmailSubject(data.subject || "");
      setEmailBody(data.body || "");
    } catch (e) {
      toast.error("Could not generate the email draft.");
    } finally {
      setDrafting(false);
    }
  }, [selected, formData]);

  // Open the compose modal (validates required fields, then drafts)
  const openCompose = () => {
    if (!selected) return;
    if (missing.length) {
      toast.error(`Fill required: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }
    const guessedTo = formData.contractor_email || formData.email || "";
    setEmailTo(guessedTo);
    setComposeOpen(true);
    fetchDraft();
  };

  const sendEmail = async () => {
    const recipients = emailTo.split(/[,\s;]+/).map((s) => s.trim()).filter((s) => s.includes("@"));
    if (!recipients.length) { toast.error("Add at least one valid email address."); return; }
    if (!emailSubject.trim()) { toast.error("Subject is required."); return; }
    setSending(true);
    try {
      await axios.post(
        `${API}/studio/send`,
        {
          template_id: selected.id, data: formData, to: recipients,
          subject: emailSubject, body: emailBody,
          from_name: fromName.trim() || undefined, bcc_me: bccMe,
        },
        { headers: { ...authHeader(), "Content-Type": "application/json" } },
      );
      toast.success(`Sent to ${recipients.join(", ")}`);
      setComposeOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to send email.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  // ===== Template gallery view =====
  if (!selected) {
    return (
      <div className="p-6" data-testid="studio-tab">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={18} className="text-[#B8860B]" strokeWidth={1.5} />
            <h3 className="font-medium text-lg">Document Studio</h3>
          </div>
          <p className="text-sm text-gray-500">
            Generate personalised, signature-ready PDFs for your clients and candidates. Fully branded, ready in 30 seconds.
          </p>
        </div>

        {templates.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            <Loader2 className="animate-spin inline mr-2" size={16} /> Loading templates…
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {templates.map((tpl) => {
              const Icon = ICONS[tpl.icon] || FileText;
              return (
                <button
                  key={tpl.id}
                  onClick={() => pickTemplate(tpl)}
                  className="group text-left border border-gray-100 hover:border-[#B8860B] rounded-lg p-5 transition-all bg-white hover:shadow-sm"
                  data-testid={`tpl-${tpl.id}`}
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-[#B8860B]/10 group-hover:bg-[#B8860B]/15 mb-4">
                    <Icon size={20} className="text-[#B8860B]" strokeWidth={1.5} />
                  </div>
                  <h4 className="font-medium text-gray-800 text-sm leading-tight">{tpl.title}</h4>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">{tpl.description}</p>
                  <div className="mt-4 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity">
                    Open →
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ===== Wizard view: form on left, preview on right =====
  return (
    <div className="p-6" data-testid="studio-wizard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={goBack} className="text-sm text-gray-500 hover:text-[#B8860B] flex items-center gap-1 mb-2" data-testid="studio-back">
            <ArrowLeft size={14} /> Back to templates
          </button>
          <h3 className="font-medium text-lg">{selected.title}</h3>
          <p className="text-xs text-gray-500">{selected.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={generatePreview}
            disabled={generating}
            className="flex items-center gap-2 border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-white px-4 py-2 text-sm rounded transition-colors disabled:opacity-50"
            data-testid="studio-preview-btn"
          >
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
            {previewUrl ? "Re-generate Preview" : "Preview"}
          </button>
          <button
            onClick={downloadPdf}
            disabled={!previewBlob}
            className="flex items-center gap-2 bg-[#B8860B] hover:bg-[#9A7209] text-white px-4 py-2 text-sm rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="studio-download-btn"
          >
            <Download size={14} /> Download
          </button>
          <button
            onClick={openCompose}
            className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-black text-white px-4 py-2 text-sm rounded transition-colors"
            data-testid="studio-email-btn"
          >
            <Mail size={14} /> Send via Email
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-4 border border-gray-100 rounded-lg p-5 bg-white">
          <h4 className="text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-3">Fill in the details</h4>
          {selected.fields.map((f) => (
            <Field
              key={f.key}
              spec={f}
              value={formData[f.key] ?? ""}
              onChange={(v) => setFormData({ ...formData, [f.key]: v })}
            />
          ))}
          {missing.length > 0 && (
            <p className="text-xs text-amber-600">
              Required to preview: {missing.map((f) => f.label).join(", ")}
            </p>
          )}
        </div>

        {/* Live Preview */}
        <div className="border border-gray-100 rounded-lg bg-gray-50 overflow-hidden flex flex-col" style={{ minHeight: "640px" }}>
          <div className="px-4 py-2.5 border-b border-gray-100 bg-white text-xs uppercase tracking-wider text-[#B8860B] font-medium flex items-center justify-between">
            <span>Live Preview</span>
            {previewUrl && <span className="text-gray-400 normal-case lowercase tracking-normal">scroll to review every page</span>}
          </div>
          {previewUrl ? (
            <iframe
              src={previewUrl}
              title="Document preview"
              className="flex-1 w-full bg-white"
              data-testid="studio-preview-iframe"
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 text-center">
              <Eye size={32} strokeWidth={1.2} className="mb-3" />
              <p className="text-sm">Fill the form and click <span className="text-[#B8860B] font-medium">Preview</span> to see your document here.</p>
              <p className="text-xs mt-2 opacity-70">The same PDF can be downloaded once you’re happy with it.</p>
            </div>
          )}
        </div>
      </div>

      {composeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="studio-compose-modal">
          <div className="bg-white rounded-lg w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-[#B8860B]" />
                <h4 className="font-medium text-gray-800 text-sm">Send "{selected.title}"</h4>
              </div>
              <button onClick={() => setComposeOpen(false)} className="text-gray-400 hover:text-gray-700" data-testid="compose-close">
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">To (comma-separated)</label>
                <input
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="name@company.com, other@company.com"
                  className="w-full border border-gray-200 px-3 py-2 text-sm rounded focus:outline-none focus:border-[#B8860B]"
                  data-testid="compose-to"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">From name (optional)</label>
                <input
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder="Sent as your name by default"
                  className="w-full border border-gray-200 px-3 py-2 text-sm rounded focus:outline-none focus:border-[#B8860B]"
                  data-testid="compose-from"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Subject</label>
                <input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full border border-gray-200 px-3 py-2 text-sm rounded focus:outline-none focus:border-[#B8860B]"
                  data-testid="compose-subject"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs text-gray-500 uppercase tracking-wider">Message</label>
                  <button
                    onClick={fetchDraft}
                    disabled={drafting}
                    className="flex items-center gap-1 text-xs text-[#B8860B] hover:underline disabled:opacity-50"
                    data-testid="compose-regenerate"
                  >
                    {drafting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    {drafting ? "Drafting…" : "Regenerate with AI"}
                  </button>
                </div>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={9}
                  className="w-full border border-gray-200 px-3 py-2 text-sm rounded focus:outline-none focus:border-[#B8860B] leading-relaxed"
                  data-testid="compose-body"
                />
                <p className="text-xs text-gray-400 mt-1">The document is attached as a PDF. The recipient downloads, signs manually, and replies to you.</p>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" checked={bccMe} onChange={(e) => setBccMe(e.target.checked)} data-testid="compose-bcc" />
                Send me a copy (BCC)
              </label>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50">
              <button onClick={() => setComposeOpen(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900" data-testid="compose-cancel">
                Cancel
              </button>
              <button
                onClick={sendEmail}
                disabled={sending || drafting}
                className="flex items-center gap-2 bg-[#B8860B] hover:bg-[#9A7209] text-white px-5 py-2 text-sm rounded transition-colors disabled:opacity-50"
                data-testid="compose-send"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sending ? "Sending…" : "Send Email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ spec, value, onChange }) {
  const label = (
    <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
      {spec.label}{spec.required && <span className="text-[#B8860B] ml-0.5">*</span>}
    </label>
  );
  const common = {
    value: value,
    onChange: (e) => onChange(e.target.value),
    className: "w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#B8860B] rounded",
    placeholder: spec.placeholder || "",
    "data-testid": `field-${spec.key}`,
  };
  if (spec.type === "textarea") {
    return <div>{label}<textarea rows={3} {...common} /></div>;
  }
  if (spec.type === "select") {
    return (
      <div>{label}
        <select {...common}>
          {(spec.options || []).map((o) => (<option key={o} value={o}>{o}</option>))}
        </select>
      </div>
    );
  }
  if (spec.type === "number") {
    return <div>{label}<input type="number" {...common} /></div>;
  }
  return <div>{label}<input type="text" {...common} /></div>;
}

export default DocumentStudioTab;
